// The App Store's remove dialog, reached from the shell's own Remove.
//
// /add-app's "Installed apps" list removes one app at a time, and that removal
// is the App Store's: its remove dialog (marketplace
// MarketplaceUninstallDependentsDialog) names every other installed app whose
// manifest declares the app and what each one loses, its Remove is the atomic
// DELETE with the server's dependency guard (sdk.removeWorkspaceApp, never
// `force`), and a 409 turns it into the blocked dialog. One removal, one
// dialog: the shell keeps no second copy of either.
//
// The dialog and its functions live in the marketplace package, which the
// shell folds lazily (pages/index.js lazySharedLib, on the first /marketplace*
// visit). So the first Remove folds the SAME context here, through the same
// add-missing fold the route folds and the chat/mail/meet folds use
// (foldSharedLibContext, shared/pages/lazyPage.js), from the same chunk a
// /marketplace visit loads: a visit before or after costs nothing twice. Then
// it mounts the dialog ONCE into the shell-root MarketplaceUninstallSlot
// (app.js). A bare key there could not resolve before the fold: DOMQL takes a
// child's extends at parent parse time (the MailOverlaySlot note in app.js),
// so the slot is filled by `set` after it.
//
// The slot is the dialog's HOST: it carries `_marketplaceHost` and its own
// state, so marketplaceHostOf binds there and every write of the store's flow
// (uninstallDepsModal, the installed-apps cache) stays on that slice, never on
// the shell root. Its finish hot-deactivates through reactivateWorkspaceModule
// (functions/workspaceApps.js), the path the store's own Uninstall takes.
//
// Returns `{ host }` (the host state, what the store's openAppUninstallDialog
// takes), or `{ reason }`: 'notReady' while the slot is not built yet,
// 'loadFailed' when the fold failed or registered no dialog. A failed fold is
// retried by the next call. `loader` is for tests; the runtime passes none.
//
// Frank note: the in-flight fold lives on the slot (one per shell root), not
// on a module binding (FA204), and the default loader is written in the body,
// not as a default parameter over a module const (ticketDetailModalLoad.js).
import _wsShared from '@symbo-ls/workspace-shared'
const shellFoldSharedLibContext = (...args) => _wsShared.pages.foldSharedLibContext(...args)

export const ensureMarketplaceUninstallDialog = async function (loader) {
  const el = this
  const rootEl = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const slot = rootEl && rootEl.MarketplaceUninstallSlot
  // A slot whose `if:` latch has not opened yet is parked with no node.
  if (!slot || !slot.node || typeof slot.set !== 'function' || !slot.state) {
    return { reason: 'notReady' }
  }
  if (slot.__mktUninstallMounted) return { host: slot.state }
  // The dialog and its opener, as the live registry holds them (a published
  // registry can carry the `smbls.` prefix).
  const registered = () => {
    const ctx = rootEl.context || {}
    const has = (reg, k) => !!reg && !!(reg[k] || reg['smbls.' + k])
    return (
      has(ctx.components, 'MarketplaceUninstallDependentsDialog') &&
      has(ctx.functions, 'openAppUninstallDialog')
    )
  }
  if (!registered()) {
    const load =
      typeof loader === 'function'
        ? loader
        : () => import('@symbo-ls/workspace-marketplace/context.js')
    if (!slot.__mktUninstallFold) {
      slot.__mktUninstallFold = Promise.resolve()
        .then(() => load())
        .then((mod) => {
          const lib = mod && mod.default ? mod.default : mod
          if (!lib || (!lib.components && !lib.functions)) {
            throw new Error('marketplace context resolved empty (stale or mismatched bundle)')
          }
          shellFoldSharedLibContext(rootEl, lib)
        })
        .catch((err) => {
          console.warn('[marketplaceUninstallDialog] fold failed:', err && err.message)
        })
        .finally(() => {
          slot.__mktUninstallFold = null
        })
    }
    await slot.__mktUninstallFold
    if (!registered()) return { reason: 'loadFailed' }
  }
  if (!slot.__mktUninstallMounted) {
    slot.__mktUninstallMounted = true
    slot.set({ extends: 'MarketplaceUninstallDependentsDialog' })
  }
  return { host: slot.state }
}
